/**
 * 应用状态管理Hook
 */
import { useState, useCallback } from 'react';
import { AppState, ConfigData, OutlineData } from '../types';
import { draftStorage } from '../utils/draftStorage';

const initialState: AppState = {
  currentStep: 0,
  config: {
    api_key: '',
    base_url: '',
    model_name: 'gpt-3.5-turbo',
  },
  fileContent: '',
  projectOverview: '',
  techRequirements: '',
  outlineData: null,
};

export const useAppState = () => {
  const [state, setState] = useState<AppState>(() => {
    const draft = draftStorage.loadDraft();
    // #region debug-point C:draft-initialized
    if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
      void fetch('http://127.0.0.1:7783/event', {
        method: 'POST',
        body: JSON.stringify({
          sessionId: 'export-project-name',
          runId: 'post-fix',
          hypothesisId: 'C',
          location: 'frontend/src/hooks/useAppState.ts:initialize',
          msg: '[DEBUG] App state initialized from active draft',
          data: {
            hasDraft: Boolean(draft),
            draftProjectName: draft?.outlineData?.project_name || '',
            draftOutlineCount: draft?.outlineData?.outline?.length || 0,
          },
          ts: Date.now(),
        }),
      }).catch(() => undefined);
    }
    // #endregion
    return {
      ...initialState,
      ...(draft || {}),
    };
  });

  const updateConfig = useCallback((config: ConfigData) => {
    setState(prev => ({ ...prev, config }));
  }, []);

  const updateStep = useCallback((step: number) => {
    setState(prev => {
      const next = { ...prev, currentStep: step };
      draftStorage.saveDraft({ currentStep: step });
      return next;
    });
  }, []);

  const updateFileContent = useCallback((fileContent: string) => {
    setState(prev => {
      const next = { ...prev, fileContent };
      draftStorage.saveDraft({ fileContent });
      return next;
    });
  }, []);

  const updateAnalysisResults = useCallback((overview: string, requirements: string) => {
    setState(prev => {
      const next = {
        ...prev,
        projectOverview: overview,
        techRequirements: requirements,
      };
      draftStorage.saveDraft({
        projectOverview: overview,
        techRequirements: requirements,
      });
      return next;
    });
  }, []);

  const updateOutline = useCallback((outlineData: OutlineData) => {
    setState(prev => {
      // #region debug-point B:outline-replaced
      if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
        void fetch('http://127.0.0.1:7783/event', {
          method: 'POST',
          body: JSON.stringify({
            sessionId: 'export-project-name',
            runId: 'post-fix',
            hypothesisId: 'B',
            location: 'frontend/src/hooks/useAppState.ts:updateOutline',
            msg: '[DEBUG] Outline state replaced',
            data: {
              previousProjectName: prev.outlineData?.project_name || '',
              nextProjectName: outlineData.project_name || '',
              previousOutlineCount: prev.outlineData?.outline?.length || 0,
              nextOutlineCount: outlineData.outline.length,
            },
            ts: Date.now(),
          }),
        }).catch(() => undefined);
      }
      // #endregion
      const next = { ...prev, outlineData };
      draftStorage.saveDraft({ outlineData });
      return next;
    });
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => {
      const nextStepValue = Math.min(prev.currentStep + 1, 2);
      const next = { ...prev, currentStep: nextStepValue };
      draftStorage.saveDraft({ currentStep: nextStepValue });
      return next;
    });
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => {
      const prevStepValue = Math.max(prev.currentStep - 1, 0);
      const next = { ...prev, currentStep: prevStepValue };
      draftStorage.saveDraft({ currentStep: prevStepValue });
      return next;
    });
  }, []);

  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  return {
    state,
    updateConfig,
    updateStep,
    updateFileContent,
    updateAnalysisResults,
    updateOutline,
    nextStep,
    prevStep,
    resetState,
  };
};
